/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useRef} from 'react';
import {error} from '@ion/core/src/logger/logger';

type CacheKey = 'fleet' | 'selected';

type UseAssetLayerAnimationProps = {
  fleetAssetLayerRef: React.MutableRefObject<__esri.FeatureLayer | null>;
  fleetSelectedAssetLayerRef: React.MutableRefObject<__esri.FeatureLayer | null>;
  selectedAssetRef: React.MutableRefObject<{assetId: number} | null>;
  isSmoothAssetMovementActiveRef: React.MutableRefObject<boolean>;
  panMapToSelectedAsset?: Function;
  view?: __esri.MapView;
  highlightFeature?: () => void;
  onFindNearbyRefresh?: () => void;
  // applies the animation frames of every moving asset in one edit per layer
  // per BATCH_EDIT_MS, instead of one edit per asset per frame ...
  batchEdits?: boolean;
};

type PendingLayerEdits = {
  graphics: Map<number, __esri.Graphic>;
  callbacks: Set<() => void>;
};

type UpdateLayerFeatureProps = {
  layer: __esri.FeatureLayer;
  graphic: __esri.Graphic;
  assetId: number;
  cacheKey: CacheKey;
  callback?: () => void;
};

const EDIT_THROTTLE_MS = 50;
const CACHE_ENABLED    = true;
const BATCH_EDIT_MS    = 100;

/**
 * useAssetLayerAnimation
 *
 * React hook that manages smooth, sequential animation of asset graphics
 * on ArcGIS FeatureLayers using `applyEdits`.
 *
 * Responsibilities:
 * - Animates asset movement by applying incremental geometry updates.
 * - Caches FeatureLayer OBJECTIDs per asset to avoid repeated queries.
 * - Throttles updates per asset to prevent excessive applyEdits calls.
 * - Serializes edits per asset using a promise queue to avoid race conditions.
 * - Updates both Fleet and Selected layers when the asset is selected.
 *
 * Behavior:
 * - Only the next animation frame (`assetGraphics[0]`) is processed per call.
 * - Updates are skipped when throttled or when smooth animation is disabled.
 * - OBJECTIDs are cached only while smooth asset movement is active.
 * - Errors from applyEdits are caught and logged safely.
 *
 * Typical usage:
 * - Call `animateAssetOnMapLayers(assetId, graphics)` on each animation tick.
 * - Provide refs for Fleet and Selected FeatureLayers.
 * - Use when animating real-time asset movement on the map.
 * 
 *  * ```typescript
 *   import {useAssetLayerAnimation} from '../hooks/useAssetLayerAnimation';
 *
 *   const {animateAssetOnMapLayers, ..} = useAssetLayerAnimation(parmas);
 *  ```
 */
export const useAssetLayerAnimation = (params: UseAssetLayerAnimationProps) => {
  const {
    isSmoothAssetMovementActiveRef,
    fleetSelectedAssetLayerRef,
    panMapToSelectedAsset,
    onFindNearbyRefresh,
    fleetAssetLayerRef,
    selectedAssetRef,
    highlightFeature,
    batchEdits = false,
    view
  } = params || {};

  const createEmptyObjectIdCache = () => ({
    fleet    : {},
    selected : {}
  });

  const objectIdCacheRef     = useRef<Record<CacheKey, Record<number, number>>>(createEmptyObjectIdCache());
  const lastApplyEditTimeRef = useRef<Record<number, number>>({});
  const editQueueRef         = useRef<Record<number, Promise<void>>>({});
  const pendingEditsRef      = useRef(new Map<__esri.FeatureLayer, PendingLayerEdits>());
  const flushTimeoutRef      = useRef<ReturnType<typeof setTimeout>>(null);

  /**
   * Applies every queued frame - one edit per layer, the latest frame per
   * feature - then runs each distinct follow-up callback once.
   */
  const flushPendingEdits = useCallback(() => {
    const pending           = pendingEditsRef.current;
    pendingEditsRef.current = new Map();
    flushTimeoutRef.current = null;
    pending.forEach(function({graphics, callbacks}, layer) {
      layer.applyEdits({updateFeatures: Array.from(graphics.values())})
        .then(function() {
          callbacks.forEach(function(callback) {
            callback();
          });
        })
        .catch(function(err) {
          error('applyEdits failed for batched asset frames', err);
        });
    });
  }, []);

  /**
   * Queues a frame for the next batched edit of its layer.
   */
  const queueLayerEdit = useCallback(({layer, oid, graphic, callback}: {
    layer: __esri.FeatureLayer;
    oid: number;
    graphic: __esri.Graphic;
    callback?: () => void;
  }) => {
    const edits = pendingEditsRef.current.get(layer) ?? {graphics: new Map(), callbacks: new Set<() => void>()};
    edits.graphics.set(oid, graphic);
    callback && edits.callbacks.add(callback);
    pendingEditsRef.current.set(layer, edits);
    if (!flushTimeoutRef.current) {
      flushTimeoutRef.current = setTimeout(flushPendingEdits, BATCH_EDIT_MS);
    }
  }, [flushPendingEdits]);

  /**
   * Reset the cache of the Object Ids
   */
  const resetObjectIdCache = useCallback(() => {
    objectIdCacheRef.current = createEmptyObjectIdCache();
  }, []);

  /**
   * Queries all objectIds from a layer by assetId.
   */
  const queryObjectIdsByAssetId = async(
    layer: __esri.FeatureLayer,
    assetId: number
  ): Promise<number[]> => {
    const query = layer.createQuery();
    query.where = `assetId = ${assetId}`;
    return layer.queryObjectIds(query);
  };

  /**
   * Get cached objectId or fetch once from layer.
   */
  const getOrFetchObjectId = useCallback(async(
    layer: __esri.FeatureLayer,
    assetId: number,
    cacheKey: 'fleet' | 'selected'
  ): Promise<number | undefined> => {
    const cache = objectIdCacheRef.current[cacheKey];
    // already cached
    if (!cache[assetId]) {
      // fetch from layer
      const ids = await queryObjectIdsByAssetId(layer, assetId);
      const oid = ids?.[0];

      if (oid && CACHE_ENABLED && isSmoothAssetMovementActiveRef.current) {
        cache[assetId] = oid;
      }

      return oid;
    }
    
    return cache[assetId];
  }, []);

  /**
   * Apply edits to a layer and update cache if objectId changes.
   */
  const updateLayerFeature = useCallback(async({
    layer,
    graphic,
    assetId,
    cacheKey,
    callback
  }: UpdateLayerFeatureProps) => {
    const oid = await getOrFetchObjectId(layer, assetId, cacheKey);

    if (oid) {
      graphic.attributes = {
        ...(graphic.attributes || {}),
        OBJECTID : oid
      };
      
      if (batchEdits) {
        queueLayerEdit({layer, oid, graphic, callback});
      } else {
        layer.applyEdits({updateFeatures: [graphic]}).then(() => {
          callback?.();
        });
      }
    }
  }, [batchEdits, queueLayerEdit]);

  /**
   * Throttle per-asset updates.
   */
  const shouldThrottle = useCallback((assetId: number): boolean => {
    const now  = Date.now();
    const last = lastApplyEditTimeRef.current[assetId] ?? 0;

    if (now - last >= EDIT_THROTTLE_MS) {
      lastApplyEditTimeRef.current[assetId] = now;
      return false;
    };

    return true;
  }, [EDIT_THROTTLE_MS]);

  /**
   * Push work into per-asset sequential queue.
   */
  const enqueueAssetJob = useCallback((assetId: number, job: () => Promise<void>) => {
    if (!editQueueRef.current[assetId]) {
      editQueueRef.current[assetId] = Promise.resolve();
    }
    editQueueRef.current[assetId] = editQueueRef.current[assetId].then(async() => {
      if (isSmoothAssetMovementActiveRef.current) {
        await job();
      }
    });
  }, [isSmoothAssetMovementActiveRef.current]);

  /**
   * Applies the next animation frame for an asset by updating its graphic
   * on the Fleet and (if selected) Selected FeatureLayers.
   *
   * The function:
   * - Skips updates if throttled or no valid graphics are provided.
   * - Processes only the next frame (`assetGraphics[0]`).
   * - Queues the update to avoid race conditions.
   * - Updates the Fleet layer, and the Selected layer when the asset is selected.
   * - Handles errors from applyEdits safely.
   **/
  const animateAssetOnMapLayers = useCallback((assetId: number, assetGraphics: __esri.Graphic[]) => {
    const fleetLayer    = fleetAssetLayerRef.current;
    const selectedLayer = fleetSelectedAssetLayerRef.current;
    const selectedAsset = selectedAssetRef.current;

    if (fleetLayer && assetGraphics?.length && !shouldThrottle(assetId)) {
      const frameGraphic = assetGraphics[0];

      enqueueAssetJob(assetId, async() => {
        try {
          // --- Update Fleet Layer ---
          await updateLayerFeature({
            assetId,
            layer    : fleetLayer,
            graphic  : frameGraphic.clone(),
            cacheKey : 'fleet',
            callback : onFindNearbyRefresh
          });

          // --- Update Selected Layer ---
          if (selectedLayer && selectedAsset?.assetId === assetId) {
            view?.popup?.close?.();

            await updateLayerFeature({
              assetId,
              layer    : selectedLayer,
              graphic  : frameGraphic.clone(),
              cacheKey : 'selected',
              callback : highlightFeature
            });
            panMapToSelectedAsset?.(frameGraphic?.attributes, true);
          }
        } catch (err) {
          error(`applyEdits failed for asset ${assetId}`, err);
        }
      });
    }
  }, [highlightFeature, onFindNearbyRefresh, view]);

  return {
    animateAssetOnMapLayers,
    lastApplyEditTimeRef,
    resetObjectIdCache,
    objectIdCacheRef,
    editQueueRef
  };
};
